import clsx from 'clsx'
import { forwardRef, ReactNode, useId, useState } from 'react'

import { CaretDownIcon } from '../icons/CaretDownIcon'
import { Tip } from '../Tip'

type Props = {
  children: ReactNode
  bottom?: ReactNode
  className?: string
}
export function DocumentTreeView({ children, bottom, className }: Props) {
  return (
    <nav
      aria-label="Folder sidebar"
      className={clsx('flex flex-col gap-4 overflow-hidden pb-4', className)}
    >
      <div className="grow overflow-y-scroll">{children}</div>
      <div className="px-4">{bottom}</div>
    </nav>
  )
}

type DocumentTreeViewSectionProps = {
  title: ReactNode
  children?: ReactNode
  hasChildren?: boolean
  isExpanded?: boolean
  ariaLabel?: string
  ariaBusy?: boolean
}
export function DocumentTreeViewSection({
  title,
  children,
  hasChildren = true,
  isExpanded: defaultIsExpanded = true,
  ariaLabel,
  ariaBusy
}: DocumentTreeViewSectionProps) {
  const [isExpanded, setExpanded] = useState(defaultIsExpanded)
  const contentId = useId()
  return (
    <div className="flex flex-col">
      <div
        className="flex h-9 cursor-pointer items-center gap-x-2 pl-4 pt-2 uppercase"
        role="button"
        aria-label={ariaLabel}
        aria-busy={ariaBusy}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setExpanded(!isExpanded)}
      >
        {hasChildren && (
          <CaretDownIcon
            className={clsx('h-3 w-3', isExpanded ? 'rotate-0' : '-rotate-90', 'cursor-pointer')}
            aria-hidden="true"
          />
        )}
        <div className="grow text-sm font-semibold text-gray-700">{title}</div>
      </div>

      {children && isExpanded ? (
        <div id={contentId} className="flex flex-col">
          {children}
        </div>
      ) : null}
    </div>
  )
}

type DocumentTreeViewItemProps = {
  isSelected?: boolean
  isUnread?: boolean
  leading?: ReactNode
  children?: ReactNode
  badges?: ReactNode
  menu?: ReactNode
  tip?: string
  onClick?: () => void
}
export const DocumentTreeViewItem = forwardRef<HTMLDivElement, DocumentTreeViewItemProps>(
  ({ isSelected, isUnread, leading, children, badges, menu, tip, onClick }, ref) => {
    const isInteractive = Boolean(onClick)
    const content = (
      <>
        <div className="grow overflow-hidden text-ellipsis text-nowrap">{children}</div>
        {badges && <div className="flex shrink-0 items-center">{badges}</div>}
      </>
    )

    return (
      <Tip title={tip} placement="right" active={tip != undefined}>
        <div
          ref={ref}
          className={clsx(
            'group border-l-4 text-sm',
            'cursor-pointer hover:bg-stone-100',
            isSelected ? 'border-blue-500 bg-blue-100' : 'border-transparent',
            isUnread ? 'font-semibold' : undefined
          )}
        >
          <div className="flex h-8 grow items-center gap-2 bg-transparent pl-8">
            {leading && <div className="flex shrink-0 items-center">{leading}</div>}
            {isInteractive ? (
              <button
                type="button"
                className="flex min-w-0 grow items-center gap-2 bg-transparent text-left"
                aria-pressed={isSelected}
                onClick={onClick}
              >
                {content}
              </button>
            ) : (
              content
            )}
            {menu && <div className="flex shrink-0 items-center">{menu}</div>}
          </div>
        </div>
      </Tip>
    )
  }
)
